import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { API_URL } from "../../../config";

// For project selection list only
function getProjectDisplayName(project) {
  if (!project || typeof project !== "object") return "Unnamed Project";
  return (
    project.projectName ||
    project.name ||
    project.project_title ||
    project.project_name ||
    project.title ||
    project.projectType ||
    project.project_type ||
    project.serviceName ||
    project.project ||
    (project.service && project.service.serviceName) ||
    (typeof project.service === "string" && project.service) ||
    (Array.isArray(project.projectCategory) && project.projectCategory.length
      ? project.projectCategory.join(", ")
      : undefined) ||
    String(project) ||
    "Unnamed Project"
  );
}

function InvoiceGenerator() {
  const { clientId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const client = location.state?.client || {};

  const [projects, setProjects] = useState([]);
  const [companyEmail, setCompanyEmail] = useState("");
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [dueDate, setDueDate] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("UPI");
  const [currency, setCurrency] = useState("INR");
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [errors, setErrors] = useState({});
  const [emailSent, setEmailSent] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [creatorEmail, setCreatorEmail] = useState("");
  const [addGstNumber, setAddGstNumber] = useState(false);
  const [taxName, setTaxName] = useState("");
  const [taxAmount, setTaxAmount] = useState("");

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (userData?.email) setCreatorEmail(userData.email);
  }, []);

  useEffect(() => {
    if (!clientId) return;
    axios
      .get(`${API_URL}/api/getProjectbyClient/${clientId}`)
      .then((res) => setProjects(res.data || []))
      .catch(() =>
        setErrors((e) => ({ ...e, fetchProjects: "Failed to load projects." }))
      );
  }, [clientId]);

  const handleToggle = (projectId) => {
    setErrors({});
    setSelectedProjects((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId]
    );
  };

  useEffect(() => {
    axios
      .get(`${API_URL}/api/getCompnayDetails`)
      .then((res) => {
        if (res.data?.[0]?.email) {
          setCompanyEmail(res.data[0].email);
        }
      })
      .catch(() => {
        console.warn("Company email not found");
      });
  }, []);

  const handleGenerateInvoice = () => {
    const e = {};

    if (!client?.emailId) e.clientEmail = "Client email missing.";
    if (!companyEmail) e.companyEmail = "Company email missing.";
    if (!dueDate) e.dueDate = "Due date required.";
    if (!selectedProjects.length) e.projects = "Select at least one project.";

    setErrors(e);
    if (Object.keys(e).length > 0) {
      alert("Please fill all required fields.");
      return;
    }

    // ✅ DEFINE payloadProjects (THIS WAS MISSING)
    const payloadProjects = selectedProjects.map((id) => {
      const proj = projects.find((p) => `${p._id}` === `${id}`);
      return {
        projectId: id,
        projectName: getProjectDisplayName(proj),
        amount: Number(
          proj?.project_price || proj?.price || proj?.amount || 0
        ),
      };
    });

    setInvoice({
      clientId,
      clientName: client.leadName,
      clientEmail: client.emailId,
      clientGstNumber: addGstNumber ? (client.gstNumber || "") : "",
      clientAccountNo: addGstNumber ? (client.accountNo || "") : "",
      taxName: addGstNumber ? String(taxName || "").trim() : "",
      taxAmount: addGstNumber ? Number(taxAmount || 0) : 0,
      companyEmail: companyEmail,
      projects: payloadProjects, // ✅ now defined
      dueDate,
      paymentMethod,
      currency,
      createdBy: creatorEmail,
    });

    setShowPreview(true);
  };

  const handleConfirmAndSend = async () => {
    if (!invoice) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/createInvoice`, {
        ...invoice,
        sendNow: true
      });
      const created = res.data.invoice || res.data;
      setInvoice(created);
      setEmailSent(true);
      setShowPreview(false);
      alert("✅ Invoice created and sent successfully!");
    } catch {
      alert("❌ Failed to send invoice.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!invoice) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/sendInvoice/${invoice._id}`);
      setEmailSent(true);
      alert("📧 Invoice email sent successfully!");
    } catch {
      setEmailSent(false);
      alert("❌ Failed to send email.");
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = selectedProjects.reduce((sum, id) => {
    const proj = projects.find((p) => `${p._id}` === `${id}`);
    return sum + (Number(proj?.price || proj?.project_price || proj?.amount) || 0);
  }, 0);

  const minDate = new Date().toISOString().split("T")[0];
  const currSym = { INR: "₹", USD: "$", EUR: "€", GBP: "£", CAD: "CA$" }[currency] || "₹";
  const invCurrSym = { INR: "₹", USD: "$", EUR: "€", GBP: "£", CAD: "CA$" }[invoice?.currency] || "₹";

  return (
    <div className="container mt-4" style={{ maxWidth: "750px" }}>
      <div className="mb-3">
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
      </div>
      <h2 className="mb-4 text-center">Generate Invoice</h2>
      <div className="mb-2">
        <strong>Client:</strong> {client?.leadName || "Unknown"}
      </div>
      <div className="mb-2">
        <strong>Client Email:</strong>{" "}
        {client?.emailId || <span className="text-danger">missing</span>}
      </div>
      {errors.clientEmail && (
        <div className="text-danger mb-2">{errors.clientEmail}</div>
      )}
      <div className="mb-3">
        <label className="form-label fw-bold">Due Date:</label>
        <input
          type="date"
          className="form-control w-50"
          value={dueDate}
          min={minDate}
          onChange={(e) => {
            setDueDate(e.target.value);
            setErrors((s) => ({ ...s, dueDate: undefined }));
          }}
        />
        {errors.dueDate && <div className="text-danger">{errors.dueDate}</div>}
      </div>
      <div className="mb-3">
        <label className="form-label fw-bold">Payment Method:</label>
        <select
          className="form-select w-50"
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
        >
          <option value="UPI">UPI</option>
          <option value="Net Banking">Net Banking</option>
          <option value="Debit Card">Debit Card</option>
          <option value="Credit Card">Credit Card</option>
          <option value="Cash">Cash</option>
        </select>
      </div>
      <div className="mb-3">
        <label className="form-label fw-bold">Currency:</label>
        <select
          className="form-select w-50"
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
        >
          <option value="INR">INR (₹)</option>
          <option value="USD">USD ($)</option>
          <option value="EUR">EUR (€)</option>
          <option value="GBP">GBP (£)</option>
          <option value="CAD">CAD (CA$)</option>
        </select>
      </div>
      <div className="form-check mt-3">
        <input
          className="form-check-input"
          type="checkbox"
          id="addGstNumber"
          checked={addGstNumber}
          onChange={(e) => setAddGstNumber(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="addGstNumber">
          Add GST Number
        </label>
      </div>
      {addGstNumber && (
        <div className="row g-3 mt-1 mb-3">
          <div className="col-md-6">
            <label className="form-label">GST Number</label>
            <input type="text" className="form-control" value={client?.gstNumber || ""} readOnly />
          </div>
          <div className="col-md-6">
            <label className="form-label">Account Number</label>
            <input type="text" className="form-control" value={client?.accountNo || ""} readOnly />
          </div>
          <div className="col-md-6">
            <label className="form-label">Tax Name</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. GST 18%"
              value={taxName}
              onChange={(e) => setTaxName(e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Tax Amount</label>
            <input
              type="number"
              min="0"
              className="form-control"
              placeholder="Enter tax amount"
              value={taxAmount}
              onChange={(e) => setTaxAmount(e.target.value)}
            />
          </div>
        </div>
      )}
      <h5 className="mt-3">Select Projects:</h5>
      {errors.fetchProjects && (
        <div className="text-danger">{errors.fetchProjects}</div>
      )}
      {projects.length === 0 && <p className="text-muted">No projects found.</p>}
      <ul className="list-group mb-3">
        {projects.map((project) => (
          <li
            key={project._id}
            className="list-group-item d-flex justify-content-between align-items-center"
          >
            <div>
              <input
                type="checkbox"
                checked={selectedProjects.includes(project._id)}
                onChange={() => handleToggle(project._id)}
                className="me-2"
              />
              <strong>{getProjectDisplayName(project)}</strong>
              <div className="small text-muted">
                {project.department?.deptName} • {project.service?.serviceName}
              </div>
            </div>
            <div>
              {currSym}{Number(project.price || project.project_price || 0).toLocaleString()}
            </div>
          </li>
        ))}
      </ul>
      {errors.projects && (
        <div className="text-danger mb-2">{errors.projects}</div>
      )}
      <div className="mb-3 fw-bold">
        Total Selected: {currSym}{totalAmount.toLocaleString()}
      </div>
      <button
        className="btn w-100 mb-4"
        style={{
          background: "#003e6d",
          color: "white",
          border: "none",
          fontWeight: "600",
        }}
        onClick={handleGenerateInvoice}
        disabled={loading}
      >
        {loading ? "Preparing Preview..." : "Preview Invoice"}
      </button>

      {/* Preview Modal */}
      {showPreview && invoice && (
        <div
          className="modal show fade d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🧾 Invoice Preview</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPreview(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p><strong>Client:</strong> {invoice.clientName}</p>
                <p><strong>Email:</strong> {invoice.clientEmail}</p>
                <p><strong>Due Date:</strong> {invoice.dueDate}</p>
                <p><strong>Payment Method:</strong> {invoice.paymentMethod || "-"}</p>
                <p><strong>Created By:</strong> {invoice.createdBy}</p>
                <hr />
                <h6>Projects:</h6>
                <ul className="list-group mb-3">
                  {invoice.projects.map((p, i) => (
                    <li
                      key={i}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <span>{p.projectName || "Unnamed Project"}</span>
                      <input
                        type="number"
                        className="form-control form-control-sm w-25 text-end"
                        value={p.amount}
                        onChange={(e) => {
                          const newProjects = [...invoice.projects];
                          newProjects[i].amount = Number(e.target.value);
                          setInvoice({ ...invoice, projects: newProjects });
                        }}
                      />
                    </li>
                  ))}
                </ul>
                <div className="fw-bold text-end">
                  Total: {invCurrSym}
                  {invoice.projects
                    .reduce((sum, p) => sum + Number(p.amount), 0)
                    .toLocaleString()}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowPreview(false)}
                >
                  ✏️ Edit Again
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleConfirmAndSend}
                  disabled={loading}
                >
                  {loading ? "Sending..." : "✅ Confirm & Send"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* After Invoice Created */}
      {invoice && invoice._id && (
        <div className="p-4 border rounded shadow-sm bg-light mt-3">
          <h4 className="mb-3">✅ Invoice Created</h4>
          <p><strong>Invoice Number:</strong> {invoice.invoiceNumber}</p>
          <p><strong>Client:</strong> {invoice.clientName}</p>
          <p><strong>Created By:</strong> {invoice.createdBy || creatorEmail}</p>
          <p><strong>Due Date:</strong> {new Date(invoice.dueDate).toLocaleDateString()}</p>
          <p><strong>Payment Method:</strong> {invoice.paymentMethod || paymentMethod}</p>
          <p>
            <strong>Status:</strong>{" "}
            {emailSent ? (
              <span className="badge bg-success">Sent</span>
            ) : (
              <span className="badge bg-warning text-dark">{invoice.status || "Draft"}</span>
            )}
          </p>
          <p>
            <strong>Total:</strong> {invCurrSym}
            {invoice.projects
              .reduce((sum, p) => sum + Number(p.amount), 0)
              .toLocaleString()}
          </p>

          <div className="mt-3 d-flex align-items-center">
            {emailSent ? (
              <span className="badge bg-success me-2">Email Sent ✔️</span>
            ) : (
              <>
                <span className="badge bg-warning text-dark me-2">
                  Email Not Sent
                </span>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleResendEmail}
                  disabled={loading}
                >
                  Send Invoice Email
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
export default InvoiceGenerator;
